import React, { useContext, useState, useRef, useEffect } from 'react'
import { Message } from 'primereact/message'
import { Toast } from 'primereact/toast'
import server from '../../../server'
import { ToastPopUp } from '../../shared/utils'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import { Checkbox } from 'primereact/checkbox'
import originsDirect from '../../../assets/images/ORIGINS-DIRECT.png'
import originsDirectInstant from '../../../assets/images/ORIGINS-DIRECT-INSTANT.png'

const ConfirmOrderModal = ({
  orderData,
  module,
  handleSuccess,
  ordableProducts,
  pyNumber,
  onClose,
  onBack,
  onTermsAccepted,
  selectedOrderTypes,
}) => {
  const { setStatus, setBannerMsg } = useContext(AuthenticationContext)
  const [loading, setLoading] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [terms, setTerms] = useState({
    direct: {
      value1: false,
      value2: false,
      value3: false,
    },
    instant: {
      value1: false,
      value2: false,
      value3: false,
      value4: false,
    },
  })
  const toast = useRef(null)

  // Get unique selected order types
  const selectedTypes = Object.values(selectedOrderTypes || {})
  const hasDirect = selectedTypes.includes('Origins Direct')
  const hasInstant = selectedTypes.includes('Origins Direct Instant')

  // Reset terms when selectedOrderTypes changes (e.g., when orders are deleted)
  useEffect(() => {
    const newTerms = {
      direct: {
        value1: false,
        value2: false,
        value3: false,
      },
      instant: {
        value1: false,
        value2: false,
        value3: false,
        value4: false,
      },
    }
    setTerms(newTerms)
    setAcceptTerms(false)
    onTermsAccepted(false)
  }, [selectedOrderTypes, onTermsAccepted])

  useEffect(() => {
    // Only check terms for selected order types
    let allTermsAccepted = true

    if (hasDirect) {
      allTermsAccepted =
        allTermsAccepted && Object.values(terms.direct).every(Boolean)
    }

    if (hasInstant) {
      allTermsAccepted =
        allTermsAccepted && Object.values(terms.instant).every(Boolean)
    }

    setAcceptTerms(allTermsAccepted)
    onTermsAccepted(allTermsAccepted)
  }, [terms, onTermsAccepted, hasDirect, hasInstant])

  const handleAcceptAll = () => {
    const newTerms = {
      direct: {
        value1: hasDirect,
        value2: hasDirect,
        value3: hasDirect,
      },
      instant: {
        value1: hasInstant,
        value2: hasInstant,
        value3: hasInstant,
        value4: hasInstant,
      },
    }
    setTerms(newTerms)
    setAcceptTerms(true)
    onTermsAccepted(true)
  }

  const handleOrder = () => {
    // Only check terms for selected order types
    let allTermsAccepted = true

    if (hasDirect) {
      allTermsAccepted =
        allTermsAccepted && Object.values(terms.direct).every(Boolean)
    }

    if (hasInstant) {
      allTermsAccepted =
        allTermsAccepted && Object.values(terms.instant).every(Boolean)
    }

    if (!allTermsAccepted) {
      ToastPopUp(
        toast,
        'error',
        'Error',
        'Please accept all terms and conditions to proceed'
      )
      return
    }

    const filteredData = orderData.filter((item) => item.confirmed === true)
    const orderDetails = filteredData.map((item) => ({
      productId: item.id,
      quantity: item.quantity,
    }))

    const payload = {
      details: orderDetails,
      system: module,
    }

    if (module !== 'Origins Direct') {
      payload.pyNumber = pyNumber
    }

    setLoading(true)
    server
      .post(`/invoicesHeaders/pharmacy`, payload)
      .then((response) => {
        setLoading(false)
        onClose()
        ordableProducts(orderDetails)
        setStatus(response?.data?.userBanner?.status)
        setBannerMsg(response?.data?.userBanner?.message)
        handleSuccess()
      })
      .catch((e) => {
        setLoading(false)
        onClose()
        
        // Enhanced error handling with specific messages
        let errorTitle = 'Order Failed'
        let errorMessage = 'Your order could not be processed'
        
        if (e.response && e.response.data) {
          const errorData = e.response.data
          
          // Handle specific backend errors
          if (errorData.message) {
            if (errorData.message.includes('not available for ordering')) {
              errorTitle = 'Product Not Available'
              errorMessage = 'One or more products are not available for ordering. Please remove them and try again.'
            } else if (errorData.message.includes('not ordable')) {
              errorTitle = 'Product Not Orderable'
              errorMessage = 'Some products cannot be ordered from their manufacturers. Please check your order and try again.'
            } else if (errorData.message.includes('minimum order')) {
              errorTitle = 'Minimum Order Not Met'
              errorMessage = 'Your order does not meet the minimum order requirements for some manufacturers.'
            } else if (errorData.message.includes('payment')) {
              errorTitle = 'Payment Error'
              errorMessage = 'There was an issue processing your payment. Please check your payment details.'
            } else if (errorData.message.includes('authentication') || errorData.message.includes('unauthorized')) {
              errorTitle = 'Authentication Error'
              errorMessage = 'Your session has expired. Please log in again.'
            } else if (errorData.message.includes('network') || errorData.message.includes('timeout')) {
              errorTitle = 'Connection Error'
              errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.'
            } else {
              // Use the backend message if it's user-friendly
              errorMessage = errorData.message
            }
          }
          
          // Handle HTTP status codes
          if (e.response.status === 400) {
            errorTitle = 'Invalid Order'
            errorMessage = errorMessage || 'Please check your order details and try again.'
          } else if (e.response.status === 401) {
            errorTitle = 'Authentication Required'
            errorMessage = 'Please log in again to continue.'
          } else if (e.response.status === 403) {
            errorTitle = 'Access Denied'
            errorMessage = 'You do not have permission to place this order.'
          } else if (e.response.status === 404) {
            errorTitle = 'Service Unavailable'
            errorMessage = 'The ordering service is temporarily unavailable.'
          } else if (e.response.status >= 500) {
            errorTitle = 'Server Error'
            errorMessage = 'There was a server error. Please try again later.'
          }
        } else if (e.request) {
          // Network error
          errorTitle = 'Connection Error'
          errorMessage = 'Unable to connect to the server. Please check your internet connection.'
        } else {
          // Other errors
          errorTitle = 'Unexpected Error'
          errorMessage = 'An unexpected error occurred. Please try again.'
        }
        
        ToastPopUp(
          toast,
          'error',
          errorTitle,
          errorMessage
        )
      })
  }

  return (
    <>
      <div className='card flex justify-content-center'>
        <Toast ref={toast} />
      </div>
      <div className='row justify-content-center w-100 mx-auto my-2'>
        {hasDirect && (
          <div className='col-6'>
            <div className='card p-3 shadow-sm border-0 h-100'>
              <div className='text-center mb-3'>
                <img
                  src={originsDirect}
                  alt='Origins Direct'
                  className='img-fluid'
                  style={{ maxHeight: '40px' }}
                />
              </div>
              <ul className='list-unstyled'>
                <li className='mb-2 d-flex align-items-center gap-2'>
                  <Checkbox
                    checked={terms.direct.value1}
                    onChange={(e) =>
                      setTerms({
                        ...terms,
                        direct: { ...terms.direct, value1: e.checked },
                      })
                    }
                  />
                  <span>
                    Actual value of order may vary depending on product
                    availability
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center gap-2'>
                  <Checkbox
                    checked={terms.direct.value2}
                    onChange={(e) =>
                      setTerms({
                        ...terms,
                        direct: { ...terms.direct, value2: e.checked },
                      })
                    }
                  />
                  <span>
                    Final order will be processed and billed by the manufacturer
                    (Manufacturer's payment terms will apply)
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center gap-2'>
                  <Checkbox
                    checked={terms.direct.value3}
                    onChange={(e) =>
                      setTerms({
                        ...terms,
                        direct: { ...terms.direct, value3: e.checked },
                      })
                    }
                  />
                  <span>
                    Order cannot be changed or cancelled once submitted.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        )}
        {hasInstant && (
          <div className='col-6'>
            <div className='card p-3 shadow-sm border-0 h-100'>
              <div className='text-center mb-3'>
                <img
                  src={originsDirectInstant}
                  alt='Origins Direct Instant'
                  className='img-fluid'
                  style={{ maxHeight: '40px' }}
                />
              </div>
              <ul className='list-unstyled'>
                <li className='mb-2 d-flex align-items-center gap-2'>
                  <Checkbox
                    checked={terms.instant.value1}
                    onChange={(e) =>
                      setTerms({
                        ...terms,
                        instant: { ...terms.instant, value1: e.checked },
                      })
                    }
                  />
                  <span>
                    Actual value of order & PA may vary depending on product
                    availability
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center gap-2'>
                  <Checkbox
                    checked={terms.instant.value2}
                    onChange={(e) =>
                      setTerms({
                        ...terms,
                        instant: { ...terms.instant, value2: e.checked },
                      })
                    }
                  />
                  <span>
                    Final order will be processed and billed by the manufacturer
                    using the credit card information on file (Manufacturer's
                    payment terms will apply)
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center gap-2'>
                  <Checkbox
                    checked={terms.instant.value3}
                    onChange={(e) =>
                      setTerms({
                        ...terms,
                        instant: { ...terms.instant, value3: e.checked },
                      })
                    }
                  />
                  <span>
                    Final PA value will be remitted via electronic funds
                    transfer
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center gap-2'>
                  <Checkbox
                    checked={terms.instant.value4}
                    onChange={(e) =>
                      setTerms({
                        ...terms,
                        instant: { ...terms.instant, value4: e.checked },
                      })
                    }
                  />
                  <span>
                    Order cannot be changed or cancelled once submitted.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        )}
        <div className='row justify-content-center w-100 mx-auto my-2 mt-3'>
          <div className='col-12 d-flex align-items-center justify-content-center gap-2'>
            <Checkbox
              inputId='acceptTerms'
              checked={acceptTerms}
              onChange={(e) => {
                setAcceptTerms(e.checked)
                if (e.checked) {
                  handleAcceptAll()
                } else {
                  const newTerms = {
                    direct: {
                      value1: false,
                      value2: false,
                      value3: false,
                    },
                    instant: {
                      value1: false,
                      value2: false,
                      value3: false,
                      value4: false,
                    },
                  }
                  setTerms(newTerms)
                  onTermsAccepted(false)
                }
              }}
              className='mr-2'
            />
            <label htmlFor='acceptTerms' className='ml-2 mt-1'>
              Accept All Terms and Conditions
            </label>
          </div>
        </div>
      </div>
    </>
  )
}

export default ConfirmOrderModal
